import {computed, onUnmounted, ref} from 'vue';
import debounce from 'lodash-es/debounce';

export enum BreakPointsTypes {
    XXSmall = 'xxs',
    XSmall = 'xs',
    Small = 'sm',
    Medium = 'md',
    Large = 'lg',
    XLarge = 'xl',
}

/** Taken from Joy definitions */
export enum BreakPointsValues {
    XXSmall = 459,
    XSmall = 767,
    Small = 991,
    Medium = 1199,
    Large = 1599,
    XLarge = Infinity,
}

/**
 * Return a reactives computed variables based on browser window size

 * ### ⚠️ Only use inside `setup` function or in a composable
 *
 * @example
 *
 * ```ts
 *  const {windowWidth, isSmall, currentBreakpoint} = useWindowSize();
 * ```
 */
export const useWindowSize = () => {
    const windowWidth = ref(window?.innerWidth ?? 0);
    const windowHeight = ref(window?.innerWidth ?? 0);

    const currentBreakpoint = computed<BreakPointsTypes>(() => {
        if (windowWidth.value <= BreakPointsValues.XSmall) {
            return BreakPointsTypes.XSmall;
        } else if (windowWidth.value <= BreakPointsValues.Small) {
            return BreakPointsTypes.Small;
        } else if (windowWidth.value <= BreakPointsValues.Medium) {
            return BreakPointsTypes.Medium;
        } else if (windowWidth.value <= BreakPointsValues.Large) {
            return BreakPointsTypes.Large;
        } else if (windowWidth.value <= BreakPointsValues.XLarge) {
            return BreakPointsTypes.XLarge;
        } else {
            return BreakPointsTypes.XLarge;
        }
    });

    const handleResize = debounce(() => {
        windowWidth.value = window.innerWidth;
        windowHeight.value = window.innerHeight;
    }, 10);

    const isXsmall = computed<boolean>(() => windowWidth.value <= BreakPointsValues.XSmall);
    const isSmall = computed<boolean>(() => windowWidth.value <= BreakPointsValues.Small);
    const isMedium = computed<boolean>(() => windowWidth.value <= BreakPointsValues.Medium);
    const isLarge = computed<boolean>(() => windowWidth.value <= BreakPointsValues.Large);
    const isXLarge = computed<boolean>(() => windowWidth.value <= BreakPointsValues.XLarge);

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
    });

    handleResize();
    window.addEventListener('resize', handleResize);

    return {
        windowHeight,
        windowWidth,
        currentBreakpoint,
        isXsmall,
        isSmall,
        isMedium,
        isLarge,
        isXLarge,
    };
};
